import { useState } from "react";
import Button from "./Button";
import Tags from "./Tags";
import { AlertTriangle, CheckCircle, Clock3, Loader, Mail, Paperclip, XCircle } from "./icons";
import "./EmailParts.css";

/*
  Reusable e-mail parts (request e-mails). They show what they are given and
  never decide anything: language-neutral, all wording comes in `labels`.

    EmailStatusTag    sending / sent / failed / uncertain (+ not sent yet)
    RecipientSummary  which applications get the message and to whom; the
                      applications that have no usable address are listed as errors
    EmailPreview      To, Cc, subject, attachments (name + size only) and the body
                      (HTML in a fully sandboxed frame, plain text otherwise)
    EmailHistory      sent e-mails with their attempts, optional retry

  `sent` means "accepted by the mail provider", not "delivered": the labels
  say so (see unifiedText email.status).
*/

const LOOK = {
  sending: { variant: "info", icon: Loader },
  sent: { variant: "success", icon: CheckCircle },
  failed: { variant: "danger", icon: XCircle },
  uncertain: { variant: "warning", icon: AlertTriangle },
  none: { variant: "neutral", icon: Clock3 },
};

export function EmailStatusTag({ status, labels, size = "sm" }) {
  const look = LOOK[status] || LOOK.none;
  const Icon = look.icon;
  return (
    <Tags variant={look.variant} appearance="soft" size={size} icon={<Icon size={13} aria-hidden="true" />} className={`ui-email-status is-${status || "none"}`}>
      {labels.status[status] || labels.status.none}
    </Tags>
  );
}

const pick = (label, lang) => (label ? label[lang === "ar" ? "ar" : "en"] || label.en || label.ar : "");

export function RecipientSummary({ applications = [], missing = [], messages = [], warnings = [], labels, lang }) {
  const byApp = new Map(messages.map((message) => [message.application, message]));
  return (
    <section className="ui-email-recipients" aria-label={labels.recipients}>
      <h4>{labels.recipients}</h4>
      <ul>
        {applications.map((app) => {
          const message = byApp.get(app.application);
          return (
            <li key={app.application}>
              <strong>{pick(app.label, lang)}</strong>
              {message && (
                <span className="ui-email-addresses" dir="ltr">
                  {labels.to}: {message.to.join(", ")}
                  {message.cc?.length ? ` · ${labels.cc}: ${message.cc.join(", ")}` : ""}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {missing.length > 0 && (
        <div className="ui-email-missing" role="alert">
          <strong>{labels.missingTitle}</strong>
          <ul>
            {missing.map((item) => (
              <li key={`${item.application}-${item.code}`}>
                {pick(item.label, lang) || item.application}: {labels.missing[item.code] || item.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.map((warning) => (
        <p key={warning.code} className="ui-email-warning">
          {labels.warnings?.[warning.code] || warning.message}
        </p>
      ))}
    </section>
  );
}

const formatSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : bytes >= 1024 ? `${Math.round(bytes / 1024)} KB` : `${bytes} B`);

export function EmailPreview({ message, labels, lang, className = "" }) {
  return (
    <article className={`ui-email-preview ${className}`}>
      <dl>
        <dt>{labels.to}</dt>
        <dd dir="ltr">{(message.to || []).join(", ") || "—"}</dd>
        {message.cc?.length > 0 && (
          <>
            <dt>{labels.cc}</dt>
            <dd dir="ltr">{message.cc.join(", ")}</dd>
          </>
        )}
        <dt>{labels.subject}</dt>
        <dd dir="auto">{message.subject}</dd>
        {message.attachments?.length > 0 && (
          <>
            <dt>{labels.attachments}</dt>
            <dd>
              <ul className="ui-email-attachments">
                {message.attachments.map((file) => (
                  <li key={`${file.filename}-${file.sha256}`}>
                    <Paperclip size={13} aria-hidden="true" /> <span dir="auto">{file.filename}</span> <small>({formatSize(file.size)})</small>
                  </li>
                ))}
              </ul>
            </dd>
          </>
        )}
      </dl>
      <div className="ui-email-body" lang={lang}>
        {message.html ? (
          // the message HTML is shown fully sandboxed: no scripts, no navigation, no forms
          <iframe title={labels.body} sandbox="" srcDoc={message.html} className="ui-email-frame" />
        ) : (
          <pre dir="auto">{message.text}</pre>
        )}
      </div>
    </article>
  );
}

const when = (value, lang) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "";
};

export function EmailHistory({ emails = [], labels, lang, onRetry, retryable, busyId = null, className = "" }) {
  const [open, setOpen] = useState(null);
  if (!emails.length) return <p className="ui-email-empty">{labels.empty}</p>;
  return (
    <ul className={`ui-email-history ${className}`} aria-label={labels.history}>
      {emails.map((email) => {
        const expanded = open === email.id;
        return (
          <li key={email.id} className="ui-email-item">
            <div className="ui-email-item-head">
              <strong>{pick(email.label, lang) || email.application || labels.email}</strong>
              <EmailStatusTag status={email.status} labels={labels} />
              <span className="ui-email-when">{when(email.sentAt || email.createdAt, lang)}</span>
            </div>
            <p className="ui-email-subject" dir="auto">{email.message.subject}</p>
            <p className="ui-email-addresses" dir="ltr">{labels.to}: {(email.message.to || []).join(", ")}</p>
            {email.status === "sent" && <p className="ui-email-note">{labels.acceptedNote}</p>}
            {email.status === "uncertain" && <p className="ui-email-note warn">{labels.uncertainNote}</p>}
            {email.lastError && email.status !== "sent" && <p className="ui-email-note warn" role="status">{email.lastError}</p>}
            {email.attempts?.length > 0 && (
              <ol className="ui-email-attempts" aria-label={labels.attempts}>
                {email.attempts.map((attempt) => (
                  <li key={attempt.number}>
                    {labels.attempt} {attempt.number}: {labels.result[attempt.result] || attempt.result}
                    {attempt.errorKind ? ` · ${labels.errorKind[attempt.errorKind] || attempt.errorKind}` : ""}
                    {attempt.by?.name || attempt.by?.role ? ` · ${[attempt.by.name, attempt.by.role].filter(Boolean).join(" · ")}` : ""}
                    {attempt.finishedAt || attempt.startedAt ? ` · ${when(attempt.finishedAt || attempt.startedAt, lang)}` : ""}
                  </li>
                ))}
              </ol>
            )}
            <div className="ui-email-item-actions">
              {(email.message.text || email.message.html) && (
                <Button size="sm" appearance="text" variant="neutral" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : email.id)}>
                  {expanded ? labels.hideMessage : labels.viewMessage}
                </Button>
              )}
              {onRetry && retryable?.(email) && (
                <Button size="sm" appearance="soft" variant="primary" icon={<Mail size={14} />} loading={busyId === email.id} disabled={Boolean(busyId)} onClick={() => onRetry(email)}>
                  {labels.retry}
                </Button>
              )}
            </div>
            {expanded && <EmailPreview message={email.message} labels={labels} lang={lang} />}
          </li>
        );
      })}
    </ul>
  );
}
